import { useNavigate } from "react-router-dom";
import {
  Button,
  Container,
  CTABand,
  Eyebrow,
  IconBell,
  IconCalendar,
  IconMessage,
} from "@constructorfleet/ui/design-system";
import { useOfferings } from "../offerings/use-offerings";
import { trackClick } from "../lib/analytics";
import { HomeHeroViz } from "../components/home-hero-viz";

export function HomePage() {
  const navigate = useNavigate();
  const { offerings } = useOfferings();
  const primaryOffering = offerings[0];
  const bhgOffering = offerings.find((o) => o.name === "BHG Check-in Assist");
  const uaOffering = offerings.find(
    (offering) =>
      offering.name === "UA Notifications" ||
      offering.routes.productPath.includes("ua-notifications")
  );

  const openOffering = (productPath: string, label: string) => {
    try {
      trackClick(label, `/${productPath}`);
    } catch {
      // analytics failures should not block navigation
    }
    navigate(`/${productPath}`);
  };

  return (
    <div className="home-page">
      {/* ── 1. Hero ──────────────────────────────────────────────── */}
      <section className="hero">
        <Container>
          <div className="hero-grid">
            <div>
              <span className="eyebrow">
                <span className="pulse" />
                ConstructorFleet
              </span>
              <h1 className="display">
                The daily obligations you can&apos;t afford to miss&nbsp;— handled.
              </h1>
              <p className="lede">
                We automate the calls, check-ins, and reporting deadlines that carry real
                consequences when they slip through the cracks.
              </p>
              <div className="cta-row">
                {primaryOffering ? (
                  <Button
                    size="lg"
                    variant="brand"
                    onClick={() =>
                      openOffering(primaryOffering.routes.productPath, "See exactly how it works")
                    }
                  >
                    See exactly how it works
                  </Button>
                ) : null}
                <Button
                  size="lg"
                  variant="secondary"
                  onClick={() => {
                    const el = document.getElementById("how-platform-works");
                    el?.scrollIntoView({ behavior: "smooth" });
                  }}
                >
                  Watch it in action
                </Button>
              </div>
              <div className="trust-row">
                <div className="bit">
                  <svg viewBox="0 0 24 24">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                  No contracts
                </div>
                <div className="bit">
                  <svg viewBox="0 0 24 24">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                  Cancel anytime
                </div>
                <div className="bit">
                  <svg viewBox="0 0 24 24">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                  Full records kept
                </div>
              </div>
            </div>
            <div className="hero-viz">
              <HomeHeroViz />
            </div>
          </div>
        </Container>
      </section>

      {/* ── 2. Three pillars ─────────────────────────────────────── */}
      <section id="how-platform-works" className="section tinted">
        <Container>
          <div className="pillar-row">
            <div className="pillar">
              <span className="num">01</span>
              <h3>Never forget again</h3>
              <p>
                Your check-ins run automatically. No dialing, no remembering, no risk of missing it.
              </p>
            </div>
            <div className="pillar">
              <span className="num">02</span>
              <h3>Proof when it matters</h3>
              <p>
                Every call is recorded, timestamped, and saved. If anyone asks, you have the record.
              </p>
            </div>
            <div className="pillar">
              <span className="num">03</span>
              <h3>Know before it&apos;s too late</h3>
              <p>You&apos;ll know your status in time to act, not after the deadline has passed.</p>
            </div>
          </div>
        </Container>
      </section>

      {/* ── 3. Products ──────────────────────────────────────────── */}
      <section className="section">
        <Container>
          <div className="section-head">
            <Eyebrow brand dot>
              Products
            </Eyebrow>
            <h2 className="h1">Pick the one that fits your situation</h2>
            <p>
              Each product handles a specific daily obligation — the kind where missing it has real
              consequences.
            </p>
          </div>

          <div className="prod-grid">
            {bhgOffering ? (
              <article className="prod">
                <span className="tag">BHG Check-in Assist</span>
                <h2>Never miss your BHG medication check-in again.</h2>
                <p className="prod-lede">
                  We call the line for you, capture the result, and send it directly to you.
                </p>
                <div className="proof">
                  Today&apos;s result, saved with a transcript and audio you can download.
                </div>
                <ul className="feats">
                  <li>Calls your clinic&apos;s medication line automatically</li>
                  <li>Texts you your result immediately</li>
                  <li>Saves a full recording and transcript</li>
                  <li>Logs everything with timestamps for your records</li>
                </ul>
                <div className="ctas">
                  <Button as="a" href={`/${bhgOffering.routes.productPath}`} variant="brand">
                    See exactly how it works
                  </Button>
                </div>
              </article>
            ) : null}

            {uaOffering ? (
              <article className="prod">
                <span className="tag">UA Notifications</span>
                <h2>Know if you need to report — before your window closes.</h2>
                <p className="prod-lede">
                  We check your state&apos;s call-in line and notify you in time to act.
                </p>
                <div className="proof">
                  Your status for the day, confirmed and logged before the window closes.
                </div>
                <ul className="feats">
                  <li>Calls the correct line for your jurisdiction</li>
                  <li>Texts you your status before your deadline</li>
                  <li>Logs every check with exportable timestamps</li>
                  <li>Expanding coverage across states</li>
                </ul>
                <div className="ctas">
                  <Button
                    onClick={() =>
                      openOffering(uaOffering.routes.productPath, "See exactly how it works")
                    }
                    variant="brand"
                  >
                    See exactly how it works
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() =>
                      openOffering(
                        uaOffering.routes.signupPath ?? uaOffering.routes.productPath,
                        "Check coverage"
                      )
                    }
                  >
                    Check coverage →
                  </Button>
                </div>
              </article>
            ) : null}
          </div>
        </Container>
      </section>

      {/* ── 4. Reliability (dark) ────────────────────────────────── */}
      <section className="section dark">
        <Container>
          <div className="rel-head">
            <Eyebrow>Reliability</Eyebrow>
            <h2 className="h1" style={{ marginTop: 14 }}>
              Built for the days things go wrong
            </h2>
            <p className="lede" style={{ margin: "16px auto 0", color: "#a7adb6" }}>
              Automation only counts if it handles the bad days too. Every tool is designed to alert
              and document — not just succeed quietly and fail silently.
            </p>
          </div>
          <div className="rel-grid">
            <div className="rel-card">
              <div className="ic">
                <IconBell />
              </div>
              <h3>Notified before the call</h3>
              <p>
                You get a heads-up before the call is placed so you know it&apos;s happening — no
                surprises.
              </p>
            </div>
            <div className="rel-card">
              <div className="ic">
                <IconMessage />
              </div>
              <h3>Notified after the call</h3>
              <p>
                As soon as the call completes, you get your result. If something went wrong,
                you&apos;ll know in time to act.
              </p>
            </div>
            <div className="rel-card">
              <div className="ic">
                <IconCalendar />
              </div>
              <h3>Timestamped proof</h3>
              <p>
                Every action is logged with a timestamp. If there&apos;s ever a question about what
                happened, the record exists.
              </p>
            </div>
          </div>
        </Container>
      </section>

      {/* ── 5. Why we built this ─────────────────────────────────── */}
      <section className="section tinted">
        <Container>
          <div className="why">
            <div className="text">
              <Eyebrow brand dot>
                Why we built this
              </Eyebrow>
              <h2 style={{ marginTop: 14 }}>
                Missing a check-in isn&apos;t theoretical. It happens.
              </h2>
              <p>
                And when it does, the consequences are real — a missed window, a changed status, a
                mark on your file that you can&apos;t take back.
              </p>
              <p>Most software isn&apos;t built for that kind of pressure. This is.</p>
              <Button as="a" href="/about" variant="secondary" style={{ marginTop: 12 }}>
                More about us →
              </Button>
            </div>
            <div className="quote">
              <div>
                Your records are yours. Every action is timestamped, everything is exportable, and
                you can pull it up whenever you need it.
              </div>
              <b>— Private, logged, and always accessible</b>
            </div>
          </div>
        </Container>
      </section>

      {/* ── 6. CTA band ──────────────────────────────────────────── */}
      <section className="section">
        <Container>
          <CTABand
            title="Stop worrying about the daily call."
            lede="Get setup in under five minutes. Your first check-in runs tomorrow morning."
            ctas={[
              { label: "Get started — $15/mo", href: `/pricing`, variant: "brand" },
              {
                label: "See the demo →",
                href: `/${primaryOffering.routes.productPath}`,
                variant: "secondary",
              },
            ]}
          />
        </Container>
      </section>

      {/* ── 7. Fine print ────────────────────────────────────────── */}
      <section className="fineprint">
        <Container>
          <div className="fineprint-inner">
            ConstructorFleet operates solely as a tool for individual users and does not act on
            behalf of any clinic, provider, or treatment program. ConstructorFleet does not provide
            medical, clinical, or healthcare services. All data processed by ConstructorFleet is
            collected at the direction of the user and is not received from or maintained on behalf
            of any healthcare provider. ConstructorFleet is not affiliated with or endorsed by any
            clinic, provider, or treatment program, including BHG.
          </div>
        </Container>
      </section>
    </div>
  );
}
